// src/lib/onboarding/profileGate.js

import { supabase } from "../../supabaseClient";
import { signalBusinessProfileUpdated } from "../../hooks/useBusinessGate";

const PERSONAL_XP = 25;
const BUSINESS_XP = 75;

const PERSONAL_REWARD_KEY =
  "profile_gate_personal_complete";

const BUSINESS_REWARD_KEY =
  "profile_gate_business_complete";

const NO_BUSINESS_OPTION =
  "I don't have a business but I want to start one the right way";

const LEGAL_NATURES_REQUIRE_REG = [
  "Close Corporation",
  "Company",
  "Non-Profit Organisation",
];

function cleanText(value) {
  return String(
    value || ""
  ).trim();
}

function cleanArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) =>
      cleanText(item)
    )
    .filter(Boolean);
}

function nullableText(value) {
  const cleaned =
    cleanText(value);

  return cleaned || null;
}

async function getCurrentUserOrThrow() {
  const {
    data: { user },
    error,
  } =
    await supabase.auth.getUser();

  if (error) {
    throw error;
  }

  if (!user) {
    throw new Error(
      "Not authenticated."
    );
  }

  return user;
}

function registrationStatusFromDraft(
  draft
) {
  const explicitStatus =
    cleanText(
      draft?.registration_status
    );

  if (explicitStatus) {
    return explicitStatus;
  }

  if (
    draft?.cipc_registered ===
    true
  ) {
    return "Yes";
  }

  if (
    draft?.cipc_registered ===
    false
  ) {
    return "No";
  }

  return "";
}

function registrationBooleanFromStatus(
  status
) {
  if (status === "Yes") {
    return true;
  }

  if (status === "No") {
    return false;
  }

  // "In progress" or unanswered.
  return null;
}

async function ensureRewardAchievement(
  userId,
  achievementKey,
  xp
) {
  const {
    data: existing,
    error: selectError,
  } =
    await supabase
      .from(
        "user_achievements"
      )
      .select(
        "achievement_key"
      )
      .eq(
        "user_id",
        userId
      )
      .eq(
        "achievement_key",
        achievementKey
      )
      .maybeSingle();

  if (
    selectError &&
    selectError.code !==
      "PGRST116"
  ) {
    throw selectError;
  }

  if (
    existing?.achievement_key
  ) {
    return 0;
  }

  const {
    error: insertError,
  } =
    await supabase
      .from(
        "user_achievements"
      )
      .insert({
        user_id: userId,
        achievement_key:
          achievementKey,
        meta: {
          xp,
        },
      });

  if (
    insertError &&
    insertError.code !==
      "23505" &&
    insertError.code !==
      "409"
  ) {
    throw insertError;
  }

  return xp;
}

async function saveUserBasics(
  user,
  draft
) {
  const fullName =
    cleanText(
      draft?.full_name
    );

  const mobileNumber =
    cleanText(
      draft?.mobile_number
    );

  const province =
    cleanText(
      draft?.province
    );

  /**
   * Personal/account information belongs in public.user.
   *
   * Province represents where the learner is based,
   * not where their business is based.
   */
  const payload = {
    id: user.id,

    full_name:
      fullName ||
      user.user_metadata
        ?.full_name ||
      user.email
        ?.split("@")
        ?.[0] ||
      "Gwin User",

    email:
      user.email || "",

    mobile_number:
      mobileNumber ||
      user.user_metadata
        ?.mobile_number ||
      user.user_metadata
        ?.phone ||
      null,
  };

  /**
   * Only write province once we actually have an answer.
   *
   * This prevents an older/legacy onboarding call with an
   * empty draft from accidentally wiping a saved province.
   */
  if (province) {
    payload.province =
      province;
  }

  const { error } =
    await supabase
      .from("user")
      .upsert(
        payload,
        {
          onConflict: "id",
        }
      );

  if (error) {
    throw error;
  }
}

function buildBusinessPayload(
  userId,
  draft
) {
  const businessName =
    cleanText(
      draft?.business_name
    );

  const industry =
    cleanText(
      draft?.industry
    );

  const province =
    cleanText(
      draft?.province
    );

  const annualRevenueRange =
    cleanText(
      draft?.annual_revenue_range
    );

  const employeeCountRange =
    cleanText(
      draft?.employee_count_range
    );

  const financeTrackingMethod =
    cleanText(
      draft?.finance_tracking_method
    );

  const businessSetupType =
    cleanText(
      draft?.business_setup_type
    );

  const tradingDurationRange =
    cleanText(
      draft?.trading_duration_range
    );

  const businessBankingType =
    cleanText(
      draft?.business_banking_type
    );

  const payload = {
    user_id: userId,
  };

  /**
   * --------------------------------------------------
   * CURRENT ONBOARDING FIELDS
   * --------------------------------------------------
   *
   * Only include fields that the new onboarding
   * actually owns.
   *
   * This prevents the new onboarding from wiping
   * legacy production data with null values.
   */

  if (businessName) {
    payload.business_name =
      businessName;
  }

  if (industry) {
    payload.industry =
      industry;
  }

  if (province) {
    /**
     * public.user.province is also saved by
     * saveUserBasics().
     *
     * Keep business_profiles.province populated as
     * well because existing Gwin admin/profile/partner
     * code still reads it.
     */
    payload.province =
      province;
  }

  if (annualRevenueRange) {
    payload.annual_revenue_range =
      annualRevenueRange;
  }

  if (employeeCountRange) {
    payload.employee_count_range =
      employeeCountRange;
  }

  if (financeTrackingMethod) {
    payload.finance_tracking_method =
      financeTrackingMethod;
  }

  if (businessSetupType) {
    payload.business_setup_type =
      businessSetupType;
  }

  if (tradingDurationRange) {
    payload.trading_duration_range =
      tradingDurationRange;
  }

  if (businessBankingType) {
    payload.business_banking_type =
      businessBankingType;
  }

  /**
   * --------------------------------------------------
   * LEGACY LEGAL-NATURE COMPATIBILITY
   * --------------------------------------------------
   *
   * legal_nature is an existing PostgreSQL enum.
   *
   * Only write it when there is an exact safe mapping.
   * "Other" and "Not sure" are deliberately NOT forced
   * into the legacy enum.
   */

  const LEGAL_NATURE_MAP = {
    "Pty Ltd":
      "Company",

    "Sole proprietor":
      "Sole Prop",

    Partnership:
      "Partnership",
  };

  const mappedLegalNature =
    LEGAL_NATURE_MAP[
      businessSetupType
    ];

  if (mappedLegalNature) {
    payload.legal_nature =
      mappedLegalNature;
  } else if (!businessSetupType) {
    /**
     * Older Profile Gate compatibility.
     *
     * If this save came from an old screen instead of
     * the new onboarding, preserve the existing
     * behaviour.
     */
    const legacyLegalNature =
      cleanText(
        draft?.legal_nature
      );

    if (legacyLegalNature) {
      payload.legal_nature =
        legacyLegalNature;
    }
  }

  /**
   * --------------------------------------------------
   * LEGACY BUSINESS-AGE COMPATIBILITY
   * --------------------------------------------------
   *
   * trading_duration_range is deliberately separate
   * because its values do not map accurately onto the
   * production business_age_label enum.
   *
   * Only write business_age if an older flow actually
   * supplied a legacy-compatible value.
   */

  const legacyBusinessAge =
    cleanText(
      draft?.business_age
    );

  if (legacyBusinessAge) {
    payload.business_age =
      legacyBusinessAge;
  }

  /**
   * --------------------------------------------------
   * BANKING COMPATIBILITY
   * --------------------------------------------------
   *
   * Preserve the exact answer in business_banking_type.
   *
   * Populate the older boolean where the answer maps
   * cleanly.
   */

  if (
    businessBankingType ===
    "I use a separate account for the business"
  ) {
    payload.bank_accounts_separate =
      true;
  } else if (
    businessBankingType ===
    "I use an account that also has personal money going through it"
  ) {
    payload.bank_accounts_separate =
      false;
  } else if (
    businessBankingType ===
    "I don’t currently use a bank account for the business"
  ) {
    payload.bank_accounts_separate =
      null;
  } else if (
    typeof draft
      ?.bank_accounts_separate ===
    "boolean"
  ) {
    /**
     * Older screen compatibility.
     */
    payload.bank_accounts_separate =
      draft.bank_accounts_separate;
  }

  /**
   * --------------------------------------------------
   * LEGACY FIELDS
   * --------------------------------------------------
   *
   * These are not part of the new onboarding, but if
   * an old screen supplies them we keep supporting
   * them.
   */

  const appReason =
    cleanText(
      draft?.app_reason
    );

  if (appReason) {
    payload.app_reason =
      appReason;
  }

  const legacyMonthlyRevenueRange =
    cleanText(
      draft?.monthly_revenue_range
    );

  if (legacyMonthlyRevenueRange) {
    payload.monthly_revenue_range =
      legacyMonthlyRevenueRange;
  }

  const registrationStatus =
    registrationStatusFromDraft(
      draft
    );

  if (registrationStatus) {
    payload.registration_status =
      registrationStatus;

    payload.cipc_registered =
      registrationBooleanFromStatus(
        registrationStatus
      );
  }

  const registrationNumber =
    cleanText(
      draft?.registration_number
    );

  const legacyLegalNature =
    cleanText(
      draft?.legal_nature
    );

  const effectiveLegalNature =
    mappedLegalNature ||
    legacyLegalNature;

  const needsRegistration =
    LEGAL_NATURES_REQUIRE_REG.includes(
      effectiveLegalNature
    );

  if (
    registrationStatus ===
      "Yes" &&
    needsRegistration &&
    registrationNumber
  ) {
    payload.registration_number =
      registrationNumber;
  } else if (
    registrationStatus ===
      "No" ||
    registrationStatus ===
      "In progress"
  ) {
    payload.registration_number =
      null;
  } else if (
    registrationNumber
  ) {
    payload.registration_number =
      registrationNumber;
  }

  /**
   * Address is intentionally untouched.
   *
   * The new onboarding does not ask for an address,
   * so an existing production address must never be
   * cleared by this save.
   */

  return payload;
}

function buildOnboardingProfilePayload(
  userId,
  draft,
  {
    resumeStep,
  } = {}
) {
  return {
    user_id: userId,

    business_priorities:
      cleanArray(
        draft?.business_priorities
      ),

    confidence_cash_flow:
      nullableText(
        draft?.confidence_cash_flow
      ),

    confidence_profit_pricing:
      nullableText(
        draft?.confidence_profit_pricing
      ),

    confidence_financial_numbers:
      nullableText(
        draft?.confidence_financial_numbers
      ),

    confidence_funding_readiness:
      nullableText(
        draft?.confidence_funding_readiness
      ),

    learning_days:
      cleanArray(
        draft?.learning_days
      ),

    reminder_times:
      cleanArray(
        draft?.reminder_times
      ),

    resume_step:
      resumeStep !==
      undefined
        ? nullableText(
            resumeStep
          )
        : nullableText(
            draft?.resume_step
          ),

    resume_updated_at:
      new Date().toISOString(),

    updated_at:
      new Date().toISOString(),
  };
}

async function saveBusinessAnswers(
  userId,
  draft
) {
  const payload =
    buildBusinessPayload(
      userId,
      draft
    );

  const { error } =
    await supabase
      .from(
        "business_profiles"
      )
      .upsert(
        payload,
        {
          onConflict:
            "user_id",
        }
      );

  if (error) {
    throw error;
  }
}

async function saveLearnerOnboardingProfile(
  userId,
  draft,
  options = {}
) {
  const payload =
    buildOnboardingProfilePayload(
      userId,
      draft,
      options
    );

  const { error } =
    await supabase
      .from(
        "user_onboarding_profile"
      )
      .upsert(
        payload,
        {
          onConflict:
            "user_id",
        }
      );

  if (error) {
    throw error;
  }
}

export async function getProfileGateCompletionState() {
  const user =
    await getCurrentUserOrThrow();

  const {
    data: achievements,
    error,
  } =
    await supabase
      .from(
        "user_achievements"
      )
      .select(
        "achievement_key"
      )
      .eq(
        "user_id",
        user.id
      )
      .in(
        "achievement_key",
        [
          PERSONAL_REWARD_KEY,
          BUSINESS_REWARD_KEY,
        ]
      );

  if (error) {
    throw error;
  }

  const keys =
    new Set(
      (
        achievements ||
        []
      ).map(
        (achievement) =>
          achievement.achievement_key
      )
    );

  return {
    personalAwarded:
      keys.has(
        PERSONAL_REWARD_KEY
      ),

    businessAwarded:
      keys.has(
        BUSINESS_REWARD_KEY
      ),

    personalXp:
      keys.has(
        PERSONAL_REWARD_KEY
      )
        ? PERSONAL_XP
        : 0,

    businessXp:
      keys.has(
        BUSINESS_REWARD_KEY
      )
        ? BUSINESS_XP
        : 0,

    totalXp:
      (keys.has(
        PERSONAL_REWARD_KEY
      )
        ? PERSONAL_XP
        : 0) +
      (keys.has(
        BUSINESS_REWARD_KEY
      )
        ? BUSINESS_XP
        : 0),
  };
}

export async function getOnboardingCompletionState() {
  const user =
    await getCurrentUserOrThrow();

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "user_onboarding_profile"
      )
      .select(
        [
          "resume_step",
          "questionnaire_completed_at",
          "orientation_completed_at",
          "onboarding_completed_at",
        ].join(", ")
      )
      .eq(
        "user_id",
        user.id
      )
      .maybeSingle();

  if (
    error &&
    error.code !==
      "PGRST116"
  ) {
    throw error;
  }

  return {
    resumeStep:
      data?.resume_step ||
      "",

    questionnaireCompleted:
      Boolean(
        data
          ?.questionnaire_completed_at
      ),

    questionnaireCompletedAt:
      data
        ?.questionnaire_completed_at ||
      null,

    orientationCompleted:
      Boolean(
        data
          ?.orientation_completed_at
      ),

    orientationCompletedAt:
      data
        ?.orientation_completed_at ||
      null,

    onboardingCompleted:
      Boolean(
        data
          ?.onboarding_completed_at
      ),

    onboardingCompletedAt:
      data
        ?.onboarding_completed_at ||
      null,
  };
}

export async function awardPersonalProfileXpIfNeeded() {
  const user =
    await getCurrentUserOrThrow();

  return ensureRewardAchievement(
    user.id,
    PERSONAL_REWARD_KEY,
    PERSONAL_XP
  );
}

export async function awardBusinessProfileXpIfNeeded() {
  const user =
    await getCurrentUserOrThrow();

  return ensureRewardAchievement(
    user.id,
    BUSINESS_REWARD_KEY,
    BUSINESS_XP
  );
}

/**
 * Existing App Reason save.
 *
 * Kept for compatibility with the current Profile Gate
 * while we replace the screens.
 */
export async function saveAppReasonChoice(
  draft
) {
  const user =
    await getCurrentUserOrThrow();

  const appReason =
    cleanText(
      draft?.app_reason
    );

  if (!appReason) {
    throw new Error(
      "Please choose an option."
    );
  }

  await saveUserBasics(
    user,
    draft
  );

  const { error } =
    await supabase
      .from(
        "business_profiles"
      )
      .upsert(
        {
          user_id:
            user.id,
          app_reason:
            appReason,
        },
        {
          onConflict:
            "user_id",
        }
      );

  if (error) {
    throw error;
  }

  signalBusinessProfileUpdated(
    appReason ===
      NO_BUSINESS_OPTION
  );

  return {
    ok: true,
  };
}

/**
 * Save any answers collected so far by the new onboarding.
 *
 * This is safe to call after each step.
 */
export async function saveOnboardingDraftProgress(
  draft,
  {
    resumeStep,
  } = {}
) {
  const user =
    await getCurrentUserOrThrow();

  await saveUserBasics(
    user,
    draft
  );

  await saveBusinessAnswers(
    user.id,
    draft
  );

  await saveLearnerOnboardingProfile(
    user.id,
    draft,
    {
      resumeStep,
    }
  );

  signalBusinessProfileUpdated(
    true
  );

  return {
    ok: true,
  };
}

/**
 * Existing final Profile Gate save.
 *
 * This remains compatible with the old screens while
 * removing address as a requirement.
 */
export async function saveProfileGateDraft(
  draft
) {
  const user =
    await getCurrentUserOrThrow();

  const fullName =
    cleanText(
      draft?.full_name
    );

  const mobileNumber =
    cleanText(
      draft?.mobile_number
    );

  const appReason =
    cleanText(
      draft?.app_reason
    );

  const province =
    cleanText(
      draft?.province
    );

  if (!fullName) {
    throw new Error(
      "Full name is required."
    );
  }

  if (!mobileNumber) {
    throw new Error(
      "Phone number is required."
    );
  }

  if (!appReason) {
    throw new Error(
      "Please choose an option."
    );
  }

  if (!province) {
    throw new Error(
      "Province is required."
    );
  }

  await saveUserBasics(
    user,
    draft
  );

  const isNoBusinessUser =
    appReason ===
    NO_BUSINESS_OPTION;

  if (
    isNoBusinessUser
  ) {
    const { error } =
      await supabase
        .from(
          "business_profiles"
        )
        .upsert(
          {
            user_id:
              user.id,
            app_reason:
              appReason,
          },
          {
            onConflict:
              "user_id",
          }
        );

    if (error) {
      throw error;
    }

    await saveLearnerOnboardingProfile(
      user.id,
      draft
    );

    signalBusinessProfileUpdated(
      true
    );

    return {
      ok: true,
    };
  }

  const businessName =
    cleanText(
      draft?.business_name
    );

  const legalNature =
    cleanText(
      draft?.legal_nature
    );

  const registrationNumber =
    cleanText(
      draft?.registration_number
    );

  const industry =
    cleanText(
      draft?.industry
    );

  const annualRevenueRange =
    cleanText(
      draft?.annual_revenue_range
    );

  const monthlyRevenueRange =
    cleanText(
      draft?.monthly_revenue_range
    );

  const revenueRange =
    annualRevenueRange ||
    monthlyRevenueRange;

  const businessAge =
    cleanText(
      draft?.business_age
    );



  if (!businessName) {
    throw new Error(
      "Business name is required."
    );
  }

  if (!industry) {
    throw new Error(
      "Industry is required."
    );
  }



  if (!businessAge) {
    throw new Error(
      "Business age is required."
    );
  }

  if (!revenueRange) {
    throw new Error(
      "Revenue range is required."
    );
  }

  if (!legalNature) {
    throw new Error(
      "Legal nature is required."
    );
  }

  const needsRegistration =
    LEGAL_NATURES_REQUIRE_REG.includes(
      legalNature
    );

  if (
    needsRegistration &&
    !registrationNumber
  ) {
    throw new Error(
      "Registration number is required."
    );
  }

  /**
   * Notice there is deliberately NO address check here.
   */

  await saveBusinessAnswers(
    user.id,
    draft
  );

  await saveLearnerOnboardingProfile(
    user.id,
    draft
  );

  signalBusinessProfileUpdated(
    true
  );

  return {
    ok: true,
  };
}

export async function markQuestionnaireCompleted(
  draft
) {
  const user =
    await getCurrentUserOrThrow();

  await saveOnboardingDraftProgress(
    draft,
    {
      resumeStep:
        "onboarding-complete",
    }
  );

  const completedAt =
    new Date().toISOString();

  const { error } =
    await supabase
      .from(
        "user_onboarding_profile"
      )
      .upsert(
        {
          user_id:
            user.id,

          questionnaire_completed_at:
            completedAt,

          resume_step:
            "onboarding-complete",

          resume_updated_at:
            completedAt,

          updated_at:
            completedAt,
        },
        {
          onConflict:
            "user_id",
        }
      );

  if (error) {
    throw error;
  }

  return {
    ok: true,
    completedAt,
  };
}

export async function markOrientationCompleted() {
  const user =
    await getCurrentUserOrThrow();

  const completedAt =
    new Date().toISOString();

  const { error } =
    await supabase
      .from(
        "user_onboarding_profile"
      )
      .upsert(
        {
          user_id:
            user.id,

          orientation_completed_at:
            completedAt,

          resume_step:
            "consent-notices",

          resume_updated_at:
            completedAt,

          updated_at:
            completedAt,
        },
        {
          onConflict:
            "user_id",
        }
      );

  if (error) {
    throw error;
  }

  return {
    ok: true,
    completedAt,
  };
}

/**
 * THIS becomes the durable "let this learner into the app"
 * marker.
 */
export async function markOnboardingCompleted() {
  const user =
    await getCurrentUserOrThrow();

  const completedAt =
    new Date().toISOString();

  const { error } =
    await supabase
      .from(
        "user_onboarding_profile"
      )
      .upsert(
        {
          user_id:
            user.id,

          onboarding_completed_at:
            completedAt,

          resume_step:
            null,

          resume_updated_at:
            completedAt,

          updated_at:
            completedAt,
        },
        {
          onConflict:
            "user_id",
        }
      );

  if (error) {
    throw error;
  }

  signalBusinessProfileUpdated(
    true
  );

  return {
    ok: true,
    completedAt,
  };
}

export async function saveNotificationPreference(
  enabled
) {
  const user =
    await getCurrentUserOrThrow();

  const { error } =
    await supabase
      .from("user")
      .update({
        notifications_enabled:
          Boolean(
            enabled
          ),
      })
      .eq(
        "id",
        user.id
      );

  if (error) {
    throw error;
  }

  return {
    ok: true,
  };
}